/**
 * Exportação da FAQ da campanha para planilha (AUC-125).
 *
 * O cliente revisa as perguntas fora do sistema, então a planilha precisa ser o que o
 * doador lê -- não o que está cadastrado. Duas consequências:
 *
 *   - quem decide o conteúdo é o endpoint Faq/Tree com visible=true, o MESMO que alimenta
 *     a modal pública. A tela de admin tem outra árvore em memória (treeForAdmin), que
 *     inclui oculta e desativada de propósito; filtrar aquela aqui exigiria reimplementar
 *     a regra de visibilidade, que mistura override por campanha com tipo de campanha
 *     resolvido pela lei de incentivo.
 *
 *   - a resposta vira texto puro. Hoje 633 das 2.073 respostas cadastradas têm tag HTML,
 *     e <p>/<br>/<li> carregam a estrutura que a pessoa vê na tela -- jogar as tags fora
 *     sem traduzir grudaria parágrafos e itens de lista numa linha só.
 */

export const FAQ_EXPORT_COLUMNS = ["Categoria", "Subcategoria", "Pergunta", "Resposta"];

// Entidades nomeadas que aparecem no editor da FAQ. As numéricas (&#39; &#xE9;) são
// resolvidas pela regra geral abaixo, então esta lista só precisa cobrir as com nome.
const NAMED_ENTITIES = {
  nbsp: " ", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'",
  ldquo: "“", rdquo: "”", lsquo: "‘", rsquo: "’",
  hellip: "…", ndash: "–", mdash: "—", bull: "•"
};

function decodeEntities(text) {
  return text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    // &amp; por último seria errado -- "&amp;lt;" viraria "<". A tabela resolve tudo de
    // uma passada só, sem reprocessar o que já foi decodificado.
    .replace(/&([a-z]+);/gi, (match, name) => {
      const value = NAMED_ENTITIES[name.toLowerCase()];
      return value === undefined ? match : value;
    });
}

// Tag HTML com atributos, incluindo valor entre aspas. O ingênuo <[^>]+> erra em
// title="Doe > IR": ele para no > de dentro das aspas e deixa um pedaço do atributo
// no meio do texto. Como este regex só casa o que tem cara de tag, "5 < 10" na
// resposta continua inteiro.
const TAG = /<\/?([a-z][a-z0-9-]*)((?:\s+[^\s=/>]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*\/?>/gi;

// Tags cujo fim é uma quebra de linha na leitura.
const BLOCK_TAGS = /^(p|div|h[1-6]|ul|ol|li|tr|table|blockquote|section|article|header|footer|pre)$/i;

// Sem /g: `TAG` é global e guarda lastIndex entre chamadas, então .test() nele
// alternaria resultado a cada invocação.
const HAS_TAG = new RegExp(TAG.source, "i");

/**
 * HTML da resposta -> texto puro, preservando a estrutura visível.
 *
 * Sem DOMParser de propósito: a conversão fica determinística, roda igual em qualquer
 * ambiente e dá para testar fora do browser.
 */
export function htmlToPlainText(html) {
  if (html === null || html === undefined) return "";

  let text = String(html);

  // Saem inteiros, conteúdo junto: nunca são texto da resposta.
  text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "");
  text = text.replace(/<!--[\s\S]*?-->/g, "");

  // Em HTML, quebra de linha do fonte é só espaço -- quem quebra é <br> e o fim de
  // bloco. Sem isto, uma resposta formatada em várias linhas sairia picotada no meio
  // das frases. Vale para as 512 respostas que tem tag E quebra no fonte.
  //
  // Já resposta SEM nenhuma tag veio de texto puro, e ali a quebra foi digitada: são
  // 394 respostas assim, e achatá-las juntaria tudo numa linha só.
  if (HAS_TAG.test(text)) {
    text = text.replace(/\s*\r?\n\s*/g, " ");
  }

  text = text.replace(TAG, (match, name) => {
    const tag = name.toLowerCase();
    const fechamento = match.charAt(1) === "/";

    if (tag === "br") return "\n";
    // Marcador só na abertura; o </li> já cai na regra de bloco.
    if (tag === "li" && !fechamento) return "\n• ";
    // Célula de tabela: sem isso o conteúdo de duas colunas vira uma palavra só.
    if (tag === "td" || tag === "th") return " ";
    if (BLOCK_TAGS.test(tag)) return "\n";

    return "";
  });

  text = decodeEntities(text);

  // Normalização final. Espaço e tab colapsam. Sequência de quebras vira UMA: abertura
  // e fechamento de bloco geram duas ("<p>a</p><p>b</p>"), e linha em branco entre
  // parágrafos não ajuda quem vai revisar a planilha célula a célula.
  text = text.replace(/\r\n?/g, "\n");
  text = text.replace(/[^\S\n]+/g, " ");
  text = text.replace(/ *\n */g, "\n");
  text = text.replace(/\n+/g, "\n");

  return text.trim();
}

/**
 * Árvore Categoria -> Subcategoria -> Perguntas vira uma linha por pergunta, na mesma
 * ordem em que a FAQ exibe. Categoria e subcategoria se repetem em cada linha: a
 * planilha é para ler e filtrar, não para contemplar.
 */
export function flattenFaqTree(tree) {
  const rows = [];

  (tree || []).forEach((category) => {
    (category.subcategories || []).forEach((subcategory) => {
      (subcategory.faqs || []).forEach((faq) => {
        rows.push({
          categoria: category.name || "",
          subcategoria: subcategory.name || "",
          pergunta: htmlToPlainText(faq.question),
          resposta: htmlToPlainText(faq.answer)
        });
      });
    });
  });

  return rows;
}

/** Matriz pronta para a planilha: cabeçalho + uma linha por pergunta. */
export function buildFaqSheetData(tree) {
  return [FAQ_EXPORT_COLUMNS].concat(
    flattenFaqTree(tree).map((r) => [r.categoria, r.subcategoria, r.pergunta, r.resposta])
  );
}

/** FAQ_viasolidaria_2026-10-23.xlsx -- subdomínio quando dá para saber, id quando não. */
export function buildFaqFileName(subdomain, campaignId, today) {
  const date = (today || new Date()).toISOString().slice(0, 10);
  const slug = subdomain || "campanha-" + (campaignId || "");
  return "FAQ_" + slug + "_" + date + ".xlsx";
}
